/**
 * Regras de jogo aplicadas aos cartões dos jogadores: quais barrinhas e linhas aparecem,
 * quem vê os números e o aviso no chat quando uma barra muda. Ver docs/spec/game-rules.md.
 *
 * Tudo aqui é puro: o cliente usa para desenhar o cartão e para escrever o aviso
 * (cada cliente escreve o seu, porque o que aparece depende de quem está olhando).
 */
import { DEFAULT_GAME_RULES, DEFAULT_SESSION_RULES, GAME_RULES_LIMITS, MEMBER_BAR_COLORS, type BarRule, type GameRules, type LineRule, type MemberBar, type SessionRules } from './domain.js';

export function barRule(rules: GameRules | undefined, index: number): BarRule {
  return (rules ?? DEFAULT_GAME_RULES).bars[index] ?? { name: '', color: null, numbers: 'everyone', kind: 'values' };
}

export function lineRule(rules: GameRules | undefined, index: number): LineRule {
  return (rules ?? DEFAULT_GAME_RULES).lines[index] ?? { name: '' };
}

/** Nome da barra para mostrar; vazio vira o padrão dado (ex.: "Barrinha 2"). */
export const barName = (rules: GameRules | undefined, index: number, fallback: string): string => barRule(rules, index).name || fallback;

/** Cor da barra: a da mesa, se o mestre escolheu; senão a do cartão; senão a padrão. */
export function barColor(rules: GameRules | undefined, index: number, bar: MemberBar | null | undefined): string {
  return barRule(rules, index).color ?? bar?.color ?? MEMBER_BAR_COLORS[index] ?? MEMBER_BAR_COLORS[0];
}

export type BarViewer = { userId: string; isGm: boolean };

/** Quem olha vê os números da barra `index` do cartão de `ownerId`? */
export function canSeeBarNumbers(rules: GameRules | undefined, index: number, viewer: BarViewer, ownerId: string): boolean {
  if (barRule(rules, index).numbers === 'everyone') return true;
  return viewer.isGm || viewer.userId === ownerId;
}

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
const percentOf = (value: number, max: number) => (max > 0 ? Math.round((value / max) * 100) : 0);

/** Texto dentro da barra: "7/12", "58%", ou nada quando os números estão escondidos. */
export function barText(rules: GameRules | undefined, index: number, bar: MemberBar, visible: boolean): string {
  if (!visible) return '';
  return barRule(rules, index).kind === 'percent' ? `${percentOf(bar.value, bar.max)}%` : `${fmt(bar.value)}/${fmt(bar.max)}`;
}

/** Variação de um número: "+5", "-3", "+0"; escondida, só o sinal: "+?", "-?", "+0". */
export function deltaText(delta: number, visible: boolean, suffix = ''): string {
  if (!visible) return (delta > 0 ? '+?' : delta < 0 ? '-?' : '+0') + suffix;
  return `${delta < 0 ? '-' : '+'}${fmt(Math.abs(delta))}${suffix}`;
}

export type BarChange = { index: number; name: string; text: string };

/**
 * O que mudou nas barrinhas de um cartão, nas barras que a mesa mostra.
 * - valores: a variação do valor e, se o máximo também mudou, a dele ("+5, máx. +10");
 * - porcentagem: a variação da porcentagem mostrada ("-25%").
 * Barras que aparecem ou somem (máximo 0) não geram aviso: são configuração, não jogo.
 */
export function barChanges(
  rules: GameRules | undefined,
  before: (MemberBar | null)[],
  after: (MemberBar | null)[],
  visible: (index: number) => boolean,
  fallbackName: (index: number) => string,
  maxLabel: string,
): BarChange[] {
  const count = (rules ?? DEFAULT_GAME_RULES).barCount;
  const out: BarChange[] = [];
  for (let i = 0; i < count; i++) {
    const a = before[i];
    const b = after[i];
    if (!a || !b) continue;
    const dv = b.value - a.value;
    const dm = b.max - a.max;
    if (dv === 0 && dm === 0) continue;
    const show = visible(i);
    let text: string;
    if (barRule(rules, i).kind === 'percent') text = deltaText(percentOf(b.value, b.max) - percentOf(a.value, a.max), show, '%');
    else if (dm === 0) text = deltaText(dv, show);
    else if (dv === 0) text = `${maxLabel} ${deltaText(dm, show)}`;
    else text = `${deltaText(dv, show)}, ${maxLabel} ${deltaText(dm, show)}`;
    out.push({ index: i, name: barName(rules, i, fallbackName(i)), text });
  }
  return out;
}

/** As regras com as listas completas (uma entrada por barra e por linha possíveis). */
export function normalizeGameRules(rules: GameRules): GameRules {
  const pad = <T>(list: T[], n: number, make: () => T) => Array.from({ length: n }, (_, i) => list[i] ?? make());
  return {
    barCount: rules.barCount,
    bars: pad(rules.bars, GAME_RULES_LIMITS.bars, () => ({ name: '', color: null, numbers: 'everyone' as const, kind: 'values' as const })),
    lineCount: rules.lineCount,
    lines: pad(rules.lines, GAME_RULES_LIMITS.lines, () => ({ name: '' })),
    diceSource: rules.diceSource,
    diceAnimation: rules.diceAnimation,
    sessions: { ...DEFAULT_SESSION_RULES, ...rules.sessions },
  };
}

/**
 * O registro das sessões e os relógios da mesa (docs/spec/session-clock.md, "Ligar e desligar"), com
 * o padrão (tudo ligado) para a mesa que ainda não chegou ou que veio de um servidor antigo.
 */
export function sessionRules(room: { gameRules?: GameRules | undefined } | null | undefined): SessionRules {
  return { ...DEFAULT_SESSION_RULES, ...room?.gameRules?.sessions };
}

/**
 * Os dados 3D da mesa animam (docs/spec/dice-3d.md, "Resultado depois da animação")? A mesa que ainda
 * não chegou ou que veio de um servidor antigo anima, como antes da regra.
 */
export function diceAnimation(room: { gameRules?: GameRules | undefined } | null | undefined): boolean {
  return room?.gameRules?.diceAnimation !== false;
}
